<script setup lang="ts">
import { useId } from 'vue'

/**
 * Marque SuiviPMA (SP-21).
 *
 * Le pictogramme est une **pousse** : une tige et deux feuilles qui s'élèvent — la graine qui germe, la patience, l'espoir qui grandit, l'accompagnement du
 * parcours. Sur le dégradé terracotta → miel du design. Le logotype reste dans une seule
 * famille, celle des titres (Alegreya, demi-gras, SP-34) : « Suivi » en encre, « PMA » en
 * terracotta. Il ne suit pas le jeton du texte courant : un logotype dans la famille des
 * titres reste stable quelle que soit la sans retenue pour le texte.
 *
 * Seuls dégradés en dur tolérés hors `tailwind.css` : ceux de la marque (racine §5).
 * `public/favicon.svg` reprend ce même dessin : les deux se modifient ensemble.
 */
withDefaults(defineProps<{ wordmark?: boolean; size?: number }>(), { wordmark: true, size: 36 })

/* Un dégradé par instance : plusieurs marques sur une page ne partagent pas un `id`. */
const gradientId = `brand-${useId()}`
</script>

<template>
  <span class="inline-flex items-center gap-2.5">
    <svg
      viewBox="0 0 48 48"
      :width="size"
      :height="size"
      class="shrink-0 rounded-[30%] shadow-[0_6px_16px_rgba(184,86,75,0.35)]"
      role="img"
      aria-label="SuiviPMA"
    >
      <defs>
        <linearGradient :id="gradientId" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#d1786b" />
          <stop offset="1" stop-color="#d1934f" />
        </linearGradient>
      </defs>
      <rect width="48" height="48" rx="14" :fill="`url(#${gradientId})`" />
      <!-- La tige -->
      <path
        data-part="stem"
        d="M24 41 C24 36 24 31 24 24"
        fill="none"
        stroke="#fff"
        stroke-width="2.8"
        stroke-linecap="round"
      />
      <!-- Feuille gauche -->
      <path
        data-part="leaf-left"
        d="M24 30 C16.5 30.5 11 25 10.5 16.5 C18.5 16.5 24 22.5 24 30 Z"
        fill="#fff"
      />
      <!-- Feuille droite -->
      <path
        data-part="leaf-right"
        d="M24 24 C24 15.5 29.5 9.5 37.5 9 C37 17.5 31.5 23.5 24 24 Z"
        fill="#fff"
      />
    </svg>
    <span
      v-if="wordmark"
      class="font-serif leading-none font-semibold tracking-[-0.01em] text-foreground"
      :style="{ fontSize: `${size * 0.62}px` }"
    >
      Suivi<span class="text-primary">PMA</span>
    </span>
  </span>
</template>
